import { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { FormInput, Button, InputError, theme } from '@socrait/react';

import { ErrorBox } from '~/components/ErrorBox';
import { LeftoversModal } from '~/components/LeftoversModal';
import { useViewArchivedClassesFeature } from '~/featureFlags';

import { VoiceNameList } from './VoiceNameList';

type EditFormProps = {
  shortName: string;
  className: string;
  inputVoiceName: string;
  studentList: { id: string; voiceName: string }[];
  operation: 'add' | 'edit';
  canSaveForm: boolean;
  setShortName: (shortName: string) => void;
  setClassName: (className: string) => void;
  handleOnChangeVoiceNameText: (inputVoiceName: string) => void;
  handleVoiceNameEnter: () => void;
  setStudentList: (studentList: { id: string; voiceName: string }[]) => void;
  onSaveClass: () => void;
  setIsArchiveModalOpen: (isArchiveModalOpen: boolean) => void;
  setIsDeleteModalOpen: (isDeleteModalOpen: boolean) => void;
  isSetup?: boolean;
  duplicateName?: string;
  shortNameError?: string;
  periodError?: Error | null;
  numPeriods?: number;
  isArchivePending?: boolean;
  onBackPress?: () => void;
};

export function EditForm({
  shortName,
  className,
  inputVoiceName,
  studentList,
  operation,
  canSaveForm,
  setShortName,
  setClassName,
  handleOnChangeVoiceNameText,
  handleVoiceNameEnter,
  setStudentList,
  onSaveClass,
  setIsDeleteModalOpen,
  setIsArchiveModalOpen,
  isSetup = false,
  duplicateName,
  shortNameError,
  periodError,
  numPeriods = 0,
  isArchivePending,
  onBackPress,
}: EditFormProps) {
  const viewArchivedClasses = useViewArchivedClassesFeature();

  const [isLeftoversModalOpen, setIsLeftoversModalOpen] = useState(false);

  const hasShortNameError = !!shortNameError;

  const handleButtonPress = () => {
    if (inputVoiceName.trim().length > 0) {
      setIsLeftoversModalOpen(true);
    } else {
      onSaveClass();
    }
  };

  return (
    <>
      <View style={styles.content}>
        {periodError ? (
          <ErrorBox header="Class data unavailable!" text="Please close this window and try again." />
        ) : (
          <>
            <View style={styles.topRow}>
              <View style={styles.inputsWithError}>
                <View style={styles.inputContainer}>
                  <FormInput
                    label="Short Name"
                    placeholderText="#"
                    value={shortName}
                    onChangeText={setShortName}
                    variant="mobile"
                    canDelete={false}
                    maxLength={3}
                    containerStyle={{ maxWidth: 130 }}
                    inputStyle={styles.input}
                    required
                  />
                  <FormInput
                    label="Class Name"
                    placeholderText="Enter your class name"
                    value={className}
                    onChangeText={setClassName}
                    containerStyle={styles.classNameInputContainer}
                    inputStyle={styles.input}
                    variant="mobile"
                    canDelete={false}
                    required
                  />
                </View>
                {hasShortNameError && <InputError errorMessage={shortNameError!} containerStyle={styles.inputError} />}
              </View>
              {operation === 'edit' && numPeriods > 1 ? (
                <View style={styles.buttonContainer}>
                  {viewArchivedClasses && (
                    <Button
                      icon="archive"
                      name="Archive"
                      onPress={() => setIsArchiveModalOpen(true)}
                      containerStyle={styles.button}
                      loading={isArchivePending}
                    />
                  )}
                  <Button
                    icon="delete"
                    name="Delete"
                    variant="negative"
                    onPress={() => setIsDeleteModalOpen(true)}
                    containerStyle={styles.button}
                  />
                </View>
              ) : null}
            </View>
            <FormInput
              value={inputVoiceName}
              onChangeText={handleOnChangeVoiceNameText}
              onSubmit={handleVoiceNameEnter}
              label={operation === 'add' ? 'To add a student, type a name and press Enter:' : 'Edit Roster'}
              placeholderText={'Voice Name'}
              variant={'list'}
              hasError={duplicateName === inputVoiceName}
              errorMessage={'Duplicate voice name found. Try another name.'}
              inputStyle={styles.voiceNameInput}
              blurOnSubmit={false}
            />
            <View style={styles.voiceNamesContainer}>
              <VoiceNameList studentList={studentList} setStudentList={setStudentList} duplicateName={duplicateName} />
            </View>
            <View style={styles.buttonContainer}>
              {isSetup && <Button name="Back" onPress={onBackPress} containerStyle={styles.backButton} />}
              <Button
                name={isSetup ? 'Next' : 'Save'}
                variant="secondary"
                disabled={!canSaveForm}
                onPress={handleButtonPress}
                containerStyle={styles.saveButton}
              />
            </View>
          </>
        )}
      </View>
      <LeftoversModal
        isOpen={isLeftoversModalOpen}
        onClose={() => setIsLeftoversModalOpen(false)}
        onContinue={onSaveClass}
      />
    </>
  );
}

const styles = StyleSheet.create({
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.xlarge,
    paddingHorizontal: theme.spacing.xlarge * 2,
    paddingBottom: theme.spacing.xlarge * 2,
  },
  topRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing.xlarge * 2,
    justifyContent: 'space-between',
  },
  inputsWithError: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.medium,
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.xlarge,
  },
  input: {
    backgroundColor: theme.colors.lightGray,
  },
  classNameInputContainer: {
    flex: 1,
    minWidth: 100,
    maxWidth: 350,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.medium,
  },
  button: {
    height: 50,
  },
  voiceNamesContainer: {
    flex: 1,
  },
  voiceNameInput: {
    borderWidth: 1,
    borderColor: theme.colors.darkGray,
  },
  saveButton: {
    marginLeft: 'auto',
  },
  backButton: {
    borderWidth: 1,
    borderColor: theme.colors.darkGray,
  },
  inputError: {
    width: 'fit-content',
    height: 'initial',
    paddingVertical: theme.spacing.medium,
  },
});
